import type { CSSProperties, ReactNode, SyntheticEvent } from "react";
import { Link } from "react-router-dom";
import { Disclaimer } from "../components/Disclaimer";
import { ThemeToggle } from "../components/ThemeToggle";
import { RiskFlagList } from "../components/ui";

/*
 * The marketing landing page. Renders without the app shell on purpose: no
 * tab bar and no ChatDock (chat is authenticated and app-scoped), just a
 * minimal header, the pitch, and the same unclosable Disclaimer as every
 * other route. Copy stays inside the honesty rules the tests enforce — no
 * "top picks", no "buy now", no "recommended".
 */

/** Hide a missing image so its tinted panel reads as intentional art. */
const hideOnError = (e: SyntheticEvent<HTMLImageElement>) => {
  e.currentTarget.style.display = "none";
};

function Panel({
  src,
  alt,
  width,
  height,
  eager,
  className = "",
  rounded = "rounded-xl",
}: {
  src: string;
  alt: string;
  width: number;
  height: number;
  eager?: boolean;
  className?: string;
  rounded?: string;
}) {
  return (
    <div
      className={`${rounded} overflow-hidden border ${className}`}
      style={{
        background: "var(--surface-2)",
        borderColor: "var(--border)",
        aspectRatio: `${width} / ${height}`,
      }}
    >
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={eager ? "eager" : "lazy"}
        // React 18 only forwards the lowercase DOM attribute; the camelCase
        // prop lands in React 19.
        {...(eager ? ({ fetchpriority: "high" } as Record<string, string>) : {})}
        className="w-full h-full object-cover"
        onError={hideOnError}
      />
    </div>
  );
}

const ctaStyle: CSSProperties = { background: "var(--accent)", color: "var(--surface-0)" };

function DashboardCta({ children = "Open the dashboard" }: { children?: ReactNode }) {
  return (
    <Link
      to="/dashboard"
      className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
      style={ctaStyle}
    >
      {children} <span aria-hidden>→</span>
    </Link>
  );
}

export function Landing() {
  return (
    <div className="min-h-full flex flex-col">
      <header
        className="border-b sticky top-0 z-20"
        style={{ background: "var(--surface-1)", borderColor: "var(--border)" }}
      >
        <div className="mx-auto max-w-6xl px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-baseline gap-3">
            <span className="text-base font-semibold tracking-tight">Stock Market Blitz</span>
            <span className="hidden sm:inline text-xs" style={{ color: "var(--text-muted)" }}>
              equity factor research
            </span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Link
              to="/dashboard"
              className="rounded-md px-3 py-1.5 text-sm font-semibold transition-opacity hover:opacity-90"
              style={ctaStyle}
            >
              Open dashboard <span aria-hidden>→</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero */}
        <section className="mx-auto max-w-6xl px-4 pt-14 pb-16 lg:pt-20">
          <div className="grid gap-10 lg:grid-cols-[11fr_9fr] lg:items-center">
            <div>
              <p
                className="text-xs font-semibold uppercase tracking-[0.2em]"
                style={{ color: "var(--accent-gold)" }}
              >
                Personal factor research, in the open
              </p>
              <h1 className="display mt-4 text-4xl lg:text-5xl font-bold leading-tight">
                See exactly why every stock scores the way it does
              </h1>
              <p className="mt-5 text-lg leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                A nightly factor model that ranks a couple hundred US equities on momentum, trend
                quality, value, quality, and themes — then shows its work. Every composite number
                decomposes into the raw inputs and sources that built it.
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <DashboardCta />
                <Link
                  to="/method"
                  className="inline-flex items-center rounded-md border px-4 py-2 text-sm font-medium transition-colors"
                  style={{ borderColor: "var(--border-strong)", color: "var(--text-primary)" }}
                >
                  How the scoring works
                </Link>
              </div>
              <p className="mt-6 text-sm" style={{ color: "var(--text-muted)" }}>
                This model published its own backtest —{" "}
                <Link
                  to="/backtest"
                  className="underline underline-offset-2"
                  style={{ color: "var(--series-1)" }}
                >
                  and the backtest failed
                </Link>
                . That's the point.
              </p>
            </div>
            <Panel
              src="/img/hero-terminal.webp"
              alt="Abstract illustration of a dark research terminal with layered charts"
              width={1600}
              height={1200}
              eager
            />
          </div>
        </section>

        {/* Feature rows */}
        <section
          className="border-y"
          style={{ background: "var(--surface-1)", borderColor: "var(--border)" }}
        >
          <div className="mx-auto max-w-6xl px-4 py-16 space-y-16">
            <FeatureRow
              title="Scores you can take apart"
              img={{
                src: "/img/feature-scoring.webp",
                alt: "Illustration of a composite score decomposing into factor streams",
              }}
            >
              Five factor sleeves — momentum, trend quality, value, quality, and theme exposure —
              are weighted into one composite, and risk is always subtracted, never added. Click
              any score and it unfolds into z-scores, weights, contributions, and the raw inputs
              with their sources. Nothing is a black box.
            </FeatureRow>

            <FeatureRow
              flip
              title="A backtest that tells the truth"
              img={{
                src: "/img/feature-backtest.webp",
                alt: "Chart of a model line falling short of its benchmark",
              }}
            >
              The model tests itself against its own history and publishes the verdict — including
              the current one: it did not demonstrate predictive skill, and the result flips sign
              under ordinary methodological choices. Most tools bury that. Here it sits above every
              performance number, on purpose.
            </FeatureRow>

            <FeatureRow
              title="Risk is a penalty, never a booster"
              img={{
                src: "/img/feature-risk.webp",
                alt: "Illustration of amber warning flags attached to chart data points",
              }}
              extra={
                <div
                  className="mt-4 rounded-lg border p-3"
                  style={{ background: "var(--surface-2)", borderColor: "var(--border)" }}
                >
                  <RiskFlagList
                    flags={[
                      {
                        id: "HIGH_VOLATILITY",
                        severity: "caution",
                        message: "Annualized volatility 57% (benchmark 15%).",
                      },
                      {
                        id: "EARNINGS_IMMINENT",
                        severity: "severe",
                        message: "Reports earnings within 5 trading days.",
                      },
                    ]}
                  />
                </div>
              }
            >
              Volatility, drawdowns, imminent earnings, thin coverage — risk conditions raise flags
              that stay visible everywhere a name appears, in the ranking table and on every detail
              page. A high score never hides a flag.
            </FeatureRow>
          </div>
        </section>

        {/* Personas */}
        <section className="mx-auto max-w-6xl px-4 py-16">
          <p
            className="text-xs font-semibold uppercase tracking-[0.2em]"
            style={{ color: "var(--accent-gold)" }}
          >
            Watch the scores play out
          </p>
          <h2 className="display mt-3 text-3xl font-bold">Five virtual investors</h2>
          <p className="mt-3 max-w-2xl text-base" style={{ color: "var(--text-secondary)" }}>
            Each trades $3,000 of fake money over the same nightly data this site publishes —
            three by fixed rules at the close, one through the session from live quotes, and one
            by an AI model's own judgement — and an AI writes their diaries in character. Their
            histories — losses included — show which way of trading actually holds up.
          </p>
          <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            <PersonaCard
              img="/img/persona-max-vega.webp"
              name="Max Vega"
              style_="Aggressive"
              blurb="Momentum chaser. Volatility is opportunity, and margin is a tool — his swings are amplified in both directions."
            />
            <PersonaCard
              img="/img/persona-pru-whitfield.webp"
              name="Pru Whitfield"
              style_="Conservative"
              blurb="Quality businesses, no flagged names, no borrowing, and a cash buffer she refuses to spend."
            />
            <PersonaCard
              img="/img/persona-jordan-mead.webp"
              name="Jordan Mead"
              style_="Moderate"
              blurb="The shipped scoring, verbatim. Jordan's P&L is a live evaluation of this site's own model."
            />
            <PersonaCard
              img="/img/persona-remy-flatt.webp"
              name="Remy Flatt"
              style_="Day trader"
              blurb="Trades the intraday swings from live quotes, several times a session, and is flat by the close. Never holds a story overnight."
            />
            <PersonaCard
              img="/img/persona-iris-halden.webp"
              name="Iris Halden"
              style_="Discretionary, AI-decided"
              blurb="An AI model reads the scores, flags, themes and macro each day and decides what to buy and sell, inside hard limits the engine enforces."
            />
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <Link
              to="/personas"
              className="text-sm font-medium underline underline-offset-2"
              style={{ color: "var(--series-1)" }}
            >
              Follow the virtual investors →
            </Link>
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>
              A simulation, not a track record — and not a reason to copy any of them.
            </p>
          </div>
        </section>

        {/* Data sources */}
        <section
          className="border-t"
          style={{ background: "var(--surface-1)", borderColor: "var(--border)" }}
        >
          <div className="mx-auto max-w-6xl px-4 py-12">
            <h2 className="text-sm font-semibold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>
              Built on public, free-tier data
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {["Tiingo", "Finnhub", "FRED", "GDELT", "SEC EDGAR", "Federal Register"].map((s) => (
                <span
                  key={s}
                  className="rounded-full border px-3 py-1 text-sm"
                  style={{
                    background: "var(--surface-2)",
                    borderColor: "var(--border)",
                    color: "var(--text-secondary)",
                  }}
                >
                  {s}
                </span>
              ))}
            </div>
            <p className="mt-4 text-sm" style={{ color: "var(--text-muted)" }}>
              A nightly job scores the universe and publishes static JSON — the site itself is just
              a reader. The full pipeline is open source.
            </p>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="border-t" style={{ borderColor: "var(--border)" }}>
          <div className="mx-auto max-w-6xl px-4 py-16">
            <Panel
              src="/img/banner-data-flow.webp"
              alt="Wide illustration of nightly data flowing into a dashboard"
              width={2000}
              height={640}
              className="mb-10"
              rounded="rounded-2xl"
            />
            <div className="text-center">
              <h2 className="display text-3xl font-bold">Look at the evidence yourself</h2>
              <p className="mx-auto mt-3 max-w-xl text-base" style={{ color: "var(--text-secondary)" }}>
                Rankings, decompositions, risk flags, backtests, and three simulated portfolios —
                all in the open. None of it is investment advice.
              </p>
              <div className="mt-6">
                <DashboardCta />
              </div>
            </div>
          </div>
        </section>
      </main>

      <Disclaimer />
    </div>
  );
}

function FeatureRow({
  title,
  children,
  img,
  extra,
  flip,
}: {
  title: string;
  children: ReactNode;
  img: { src: string; alt: string };
  extra?: ReactNode;
  flip?: boolean;
}) {
  return (
    <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
      <div className={flip ? "lg:order-2" : undefined}>
        <h2 className="display text-2xl font-bold">{title}</h2>
        <p className="mt-3 text-base leading-relaxed" style={{ color: "var(--text-secondary)" }}>
          {children}
        </p>
        {extra}
      </div>
      <Panel src={img.src} alt={img.alt} width={1200} height={800} className={flip ? "lg:order-1" : undefined} />
    </div>
  );
}

function PersonaCard({
  img,
  name,
  style_,
  blurb,
}: {
  img: string;
  name: string;
  style_: string;
  blurb: string;
}) {
  return (
    <div
      className="rounded-lg border p-5"
      style={{ background: "var(--surface-1)", borderColor: "var(--border)" }}
    >
      <div
        className="h-20 w-20 overflow-hidden rounded-full border"
        style={{ background: "var(--surface-2)", borderColor: "var(--border-strong)" }}
      >
        <img
          src={img}
          alt={`Illustrated portrait of ${name}`}
          width={800}
          height={800}
          loading="lazy"
          className="h-full w-full object-cover"
          onError={hideOnError}
        />
      </div>
      <h3 className="mt-4 text-lg font-semibold">{name}</h3>
      <p className="text-xs font-medium uppercase tracking-wide" style={{ color: "var(--accent-gold)" }}>
        {style_}
      </p>
      <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
        {blurb}
      </p>
    </div>
  );
}
